import { doc, updateDoc } from "firebase/firestore";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

import { db } from "../../api/firebaseApi";
import { DATABASE } from "../../types/tables-data";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customer: {
    id: string;
    email: string;
    practiceName: string;
    case_credits: number;
  };
  onSuccess: () => void;
}

export const AdjustCreditsModal = ({
  isOpen,
  onClose,
  customer,
  onSuccess
}: Props) => {
  const [newCredits, setNewCredits] = useState<number>(customer.case_credits);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const customerRef = doc(db, DATABASE.CUSTOMERS, customer.id);
      await updateDoc(customerRef, {
        case_credits: newCredits
      });
      toast.success("Credits updated successfully");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Error updating credits", {
        description: "Please try again"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-110">
        <DialogHeader>
          <DialogTitle>Adjust Credits</DialogTitle>
          <DialogDescription>
            {customer.practiceName} · {customer.email}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="case-credits">Case Credits</Label>
          <Input
            id="case-credits"
            type="number"
            min={0}
            value={Number.isFinite(newCredits) ? newCredits : ""}
            onChange={(e) => setNewCredits(e.target.valueAsNumber)}
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isLoading || !Number.isFinite(newCredits)}
            onClick={handleSubmit}
          >
            {isLoading ? <Spinner /> : null}
            Update Credits
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
